/**
 * Native iOS app detection and app-only policy.
 *
 * The website is unchanged for normal web visitors. Everything in this file
 * only ever becomes true inside the Capacitor iOS shell, where Apple's
 * in-app-purchase rules mean we must not show prices, join/upgrade buttons
 * or any external purchase link.
 */

import { useEffect, useState } from "react";

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: Record<string, unknown>;
};

function cap(): CapacitorGlobal | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
}

/** True only inside the native app shell (iOS or a future Android build). */
export function isNativeApp(): boolean {
  const c = cap();
  if (!c) return false;
  if (typeof c.isNativePlatform === "function") return c.isNativePlatform();
  return false;
}

export function nativePlatform(): "ios" | "android" | "web" {
  const c = cap();
  const p = c?.getPlatform?.() ?? "web";
  return p === "ios" || p === "android" ? p : "web";
}

const JOURNAL_APP_KEY = "rt:native-journal-app";

/** Marks and recognises the separate Recovery Journal native wrapper. */
export function isJournalNativeApp(): boolean {
  if (!isNativeApp() || typeof window === "undefined") return false;
  const fromLaunchUrl = new URLSearchParams(window.location.search).get("journalApp") === "1";
  if (fromLaunchUrl) {
    try {
      localStorage.setItem(JOURNAL_APP_KEY, "yes");
    } catch {
      // The launch URL remains enough for this visit when storage is unavailable.
    }
    return true;
  }
  try {
    return localStorage.getItem(JOURNAL_APP_KEY) === "yes";
  } catch {
    return false;
  }
}

export function useIsJournalNativeApp(): boolean {
  const [journalApp, setJournalApp] = useState(false);
  useEffect(() => setJournalApp(isJournalNativeApp()), []);
  return journalApp;
}

/**
 * Feature switch for Apple in-app purchase. When we later ship IAP, flip this
 * to true (and wire the purchase plugin) to bring commerce back inside the app.
 */
export const NATIVE_IAP_ENABLED = false;

/**
 * Notifications switch. The device registers fine, but nothing is sent yet —
 * so the control stays hidden rather than promising something that never
 * arrives. Flip to true once sending is live.
 */
export const NATIVE_PUSH_ENABLED = false;

/** Commerce must be hidden in the app until in-app purchase exists. */
export function shouldHideCommerce(): boolean {
  return isNativeApp() && !NATIVE_IAP_ENABLED;
}

/** SSR-safe hook. Always false during server render and first paint on web. */
export function useIsNativeApp(): boolean {
  const [native, setNative] = useState(false);
  useEffect(() => {
    setNative(isNativeApp());
  }, []);
  return native;
}

/** SSR-safe hook for "hide anything to do with buying a membership". */
export function useHideCommerce(): boolean {
  const [hide, setHide] = useState(false);
  useEffect(() => {
    setHide(shouldHideCommerce());
  }, []);
  return hide;
}

/** Routes that exist to sell or take payment. Blocked inside the app. */
export const COMMERCE_ROUTES = [
  // "/signup" is intentionally NOT blocked: creating a free account is
  // allowed in the app — the page itself hides prices and external checkout
  // when shouldHideCommerce() is true and continues to the in-app Plans screen.
  "/choose-membership",
  "/complete-membership",
  "/register-membership",
  "/confirm-subscription",
  "/checkout",
  "/billing",
  "/membership",
  "/pricing",
  "/join-community",
  "/membership-benefits",
  "/membership-journey",
  "/enterprise",
  // The digital-picture shop sells digital goods through Stripe, which Apple
  // would require to go through in-app purchase. Not available in the app.
  "/shop",
];

export function isCommerceRoute(pathname: string): boolean {
  return COMMERCE_ROUTES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** External purchase destinations that must never be reachable in the app. */
const EXTERNAL_PURCHASE_HOSTS = [
  "checkout.stripe.com",
  "buy.stripe.com",
  "billing.stripe.com",
  "paypal.com",
  "www.paypal.com",
  "www.sandbox.paypal.com",
];

export function isExternalPurchaseUrl(href: string): boolean {
  try {
    const url = new URL(href, typeof window === "undefined" ? "https://recoverytrauma.com" : window.location.href);
    if (EXTERNAL_PURCHASE_HOSTS.some((h) => url.hostname === h || url.hostname.endsWith(`.${h}`))) return true;
    return isCommerceRoute(url.pathname);
  } catch {
    return false;
  }
}

/**
 * Runs before the page paints inside the iOS app shell (detected by the
 * native bridge Capacitor injects at document start). Selling pages are
 * replaced immediately, so they never flash prices while the app loads.
 */
export const NATIVE_COMMERCE_BOOT_SCRIPT = `(function(){try{var w=window.webkit;if(!(w&&w.messageHandlers&&w.messageHandlers.bridge))return;var r=document.documentElement;r.setAttribute('data-native-app','ios');if(${String(NATIVE_IAP_ENABLED)})return;r.setAttribute('data-hide-commerce','true');var p=location.pathname,l=${JSON.stringify(COMMERCE_ROUTES)};for(var i=0;i<l.length;i++){if(p===l[i]||p.indexOf(l[i]+'/')===0){location.replace('/my-membership');return;}}}catch(e){}})();`;
